import { ChevronDown, Zap } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from 'react';
import { BookingWidget, type BookingWidgetProps } from '@/components/booking/booking-widget';
import { CurrentPromotions, type PublicPromotion } from './current-promotions';
import { LastMinuteOffer, useLastMinuteOffer, type LastMinuteOfferData } from './last-minute-offer';
import { useBooking } from '@/hooks/use-booking-context';
import { queryClient } from '@/lib/queryClient';
import { validateDateRange } from '@/lib/date-utils';
import { OPEN_EMAIL_OFFER_EVENT } from '@/components/booking/booking-offer-popup';

const offerDay = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {month:"short", day:"numeric", timeZone:"UTC"});

/** Offer selection only prefills existing inputs; the existing quote and calendar remain authoritative. */
export function BookingWithOffers(props: BookingWidgetProps) {
  const booking = useBooking();
  const slug = props.propertySlug;
  const {data: offer, isLoading, isError} = useLastMinuteOffer(slug);
  const firstStay = !isLoading && !isError && offer?.active && offer.propertySlug === slug ? offer.windows?.[0] : undefined;
  const offerStatus = isLoading ? "Checking last-minute stays…" : isError ? "Last-minute availability could not be checked. Please try again." : !firstStay ? "No last-minute stays available right now" : "Choose an eligible stay below";
  const discountedStay = firstStay && Number.isFinite(firstStay.nightlyRate) && firstStay.nightlyRate > 0 && Number.isFinite(firstStay.regularNightlyRate) && firstStay.regularNightlyRate > firstStay.nightlyRate ? firstStay : undefined;
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState<{code: string; selection: number}>();
  const form = useRef<HTMLDivElement>(null);
  const latest = useRef({slug, checkIn: booking.checkIn, checkOut: booking.checkOut});
  latest.current = {slug, checkIn: booking.checkIn, checkOut: booking.checkOut};
  const sequence = useRef(0);
  const inputRevision = useRef(0);
  const onInputChange = useCallback(() => {inputRevision.current++;}, []);
  useEffect(() => () => {sequence.current++;}, []);
  const focusForm = () => {
    form.current?.scrollIntoView({block: 'start', behavior: 'auto'});
    form.current?.focus({preventScroll: true});
  };
  async function select(load: () => Promise<() => void>) {
    const id = ++sequence.current;
    const before = {...latest.current};
    const revision = inputRevision.current;
    setBusy(true); setMessage('Checking this offer…');
    try {
      const apply = await load();
      if (id !== sequence.current) return;
      if (revision !== inputRevision.current || JSON.stringify(before) !== JSON.stringify(latest.current)) {
        setMessage('Your booking details changed while checking. Please select the offer again.');
        return;
      }
      apply();
    } catch {
      if (id === sequence.current) setMessage('This offer could not be confirmed. Your booking details are unchanged. Please check the available dates or try again.');
    } finally { if (id === sequence.current) setBusy(false); }
  }
  const pickDates = (start: string, end: string) => select(async () => {
    const fresh = await queryClient.fetchQuery<LastMinuteOfferData>({queryKey: [`/api/properties/${slug}/last-minute`], staleTime: 0});
    const stay = fresh.windows?.find(w => w.start === start && w.end === end);
    if (!fresh.active || fresh.propertySlug !== slug || !stay || start > fresh.cutoff || stay.nights < stay.minNights || !validateDateRange(start,end).isValid) throw new Error('Offer unavailable');
    return () => {
      const replaced = Boolean(booking.checkIn || booking.checkOut);
      if (props.propertyId && slug) booking.setProperty(props.propertyId, slug);
      booking.updateBookingDates(start,end);
      setMessage(`${replaced ? 'Dates updated' : 'Dates selected'}: ${start} to ${end}. Review the current total below. You can edit these dates; availability is checked again before booking.`);
      focusForm();
    };
  });
  const pickPromotion = (promotion: PublicPromotion) => select(async () => {
    const fresh = await queryClient.fetchQuery<PublicPromotion[]>({queryKey: ['/api/promotions', {property: slug}], staleTime: 0});
    const promo = fresh.find(p => p.id === promotion.id);
    if (!promo) throw new Error('Offer unavailable');
    return () => {
      if (promo.config?.ctaAction === 'email_offer' || (promo.type === 'display' && promo.config?.couponCode === 'STAY5')) {
        setMessage('Complete the email offer to receive its discount. Your dates are unchanged.');
        window.dispatchEvent(new CustomEvent(OPEN_EMAIL_OFFER_EVENT));
        return;
      }
      const code = promo.config?.couponCode;
      if (code) setCoupon({code, selection: Date.now()});
      setMessage(`${promo.title} selected.${code ? ' Coupon entered; the quote will check eligibility.' : ['seasonal_rate','last_minute_rate'].includes(promo.type) ? ' Eligible rates apply automatically in your quote.' : ' Review the offer terms with your dates below.'} ${booking.checkIn && booking.checkOut ? 'Your dates are unchanged. Review the dates and total below.' : 'Choose your dates below to check eligibility.'}`);
      focusForm();
    };
  });
  return <>
    {slug && <details data-card-ignore className="group mb-4 min-w-0 rounded-2xl border border-sun/40 bg-sun/10 p-4 text-ink" data-testid="booking-offers">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink">
        <span>
          <span className="flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em]"><Zap className="h-3.5 w-3.5 shrink-0 text-sun" aria-hidden="true" />Last-minute rates &amp; offers</span>
          {discountedStay ? <span className="mt-1 block group-open:hidden"><span className="font-serif text-lg text-ink/75 line-through">${discountedStay.regularNightlyRate.toLocaleString("en-US")}</span>{" "}<span className="font-serif text-2xl">${discountedStay.nightlyRate.toLocaleString("en-US")}</span><span className="font-sans text-sm"> / night</span><span className="mt-1 block font-sans text-xs">{offerDay(discountedStay.start)}–{offerDay(discountedStay.end)} · {discountedStay.nights} nights</span></span> : <span className="mt-1 block font-sans text-xs group-open:hidden" role="status">{offerStatus}</span>}
          <span className="mt-1 block font-sans text-[12px] text-ink/75 group-open:hidden">View stays &amp; current promotions</span>
        </span>
        <ChevronDown className="h-5 w-5 shrink-0 text-sun transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <fieldset disabled={busy} className="mt-4 min-w-0 space-y-4" aria-busy={busy}>
        <div><h3 className="mb-2 font-serif text-lg">Last-minute stays</h3><LastMinuteOffer slug={slug} embedded onPick={pickDates} /></div>
        <div className="border-t border-sun/30 pt-3"><h3 className="mb-2 font-serif text-lg">Current promotions</h3><CurrentPromotions propertySlug={slug} embedded onSelect={pickPromotion} /></div>
      </fieldset>
    </details>}
    <div ref={form} tabIndex={-1} role="region" aria-label="Booking form" className="scroll-mt-28 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sun">
      <p role="status" className={message ? 'mb-3 font-sans text-sm text-ink' : 'sr-only'}>{message}</p>
      <BookingWidget {...props} selectedOfferCoupon={coupon} onOfferInputChange={onInputChange} />
    </div>
  </>;
}
